import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { filter, mergeMap, Subject, takeUntil, tap, switchMap, map, delay, EMPTY, catchError } from 'rxjs';
import { environment } from 'src/app/enviroments/enviroment';
import { AppModules, CreditRequestRoutes } from 'src/app/models/enums/routes.enum';
import { SendVerificationCodeResponse } from 'src/app/models/response/send-verification-code.response';
import { ObfuscateEmailPipe } from 'src/app/pipes/obfuscate-email.pipe';
import { ObfuscatePhonePipe } from 'src/app/pipes/obfuscate-phone.pipe';
import { AuthService } from 'src/app/services/auth.service';
import { CreditService } from 'src/app/services/credit.service';
import { DataService } from 'src/app/services/data.service';
import { ErrorModalService, ModalData } from 'src/app/services/error-modal.service';
import { SecureStorageService } from 'src/app/services/secure-storage.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { ButtonComponent } from 'src/app/shared/reusable/button/button.component';

@Component({
  selector: 'app-on-boarding-start-login',
  templateUrl: './on-boarding-start-login.component.html',
  styleUrls: ['./on-boarding-start-login.component.scss'],
  standalone: true,
  imports: [CommonModule, ButtonComponent, ReactiveFormsModule, ObfuscateEmailPipe, ObfuscatePhonePipe],
})
export class OnBoardingStartLoginComponent implements OnDestroy {
  static componentName = 'OnBoardingStartLoginComponent';
  static componentDescription =
    'Maneja la autenticación de usuario vía código SMS y gestiona la reanudación de solicitudes activas';
  public codeData$ = this.dataService.getCodeData$();
  public loading = false;
  private destroyRef$ = new Subject<boolean>();

  @Output() userLogged = new EventEmitter<string>();
  @Input() isModal = false;

  public phoneValidationForm = this.formBuilder.group({
    codePhone: new FormControl<number | undefined>(undefined, { validators: [Validators.required], nonNullable: true }),
  });

  constructor(
    private dataService: DataService,
    private formBuilder: FormBuilder,
    private spinner: SpinnerService,
    private router: Router,
    private errorModalService: ErrorModalService,
    private authService: AuthService,
    private secureStorage: SecureStorageService,
    private creditService: CreditService,
  ) {}

  ngOnDestroy(): void {
    this.destroyRef$.next(true);
    this.destroyRef$.complete();
  }

  onSubmit(): void {
    if (!this.phoneValidationForm.valid || this.loading) {
      return;
    }
    this.loading = true;
    const { codePhone } = this.phoneValidationForm.value;
    this.spinner.show('Iniciando sesión...');

    this.handleValidation(codePhone!);
  }

  private handleValidation(code: number): void {
    this.codeData$
      .pipe(
        filter((data): data is SendVerificationCodeResponse => !!data),
        switchMap((data) =>
          this.authService.loginWithCode(code, data.data.uuid).pipe(
            tap((loginResponse) => {
              const token = loginResponse.data.token;
              const userData = {
                token: token,
                cellphone: '',
                account_id: '',
                employment_type_id: 3,
              };

              this.secureStorage.storeEncrypted(environment.loggedData, userData);
              this.secureStorage.storeEncrypted(environment.loggedTokenKey, userData.token);
              this.dataService.setLoggedByUrl(true);
            }),
            takeUntil(this.destroyRef$),
            delay(100),
            switchMap((loginResponse) =>
              this.creditService.checkActiveRequest().pipe(
                map((checkActiveResponse) => ({
                  loginResponse,
                  checkActiveResponse,
                })),
              ),
            ),
          ),
        ),
      )
      .subscribe({
        next: async ({ loginResponse, checkActiveResponse }) => {
          this.spinner.hide();
          this.loading = false;
          if (checkActiveResponse.has_request === true && checkActiveResponse.last_log) {
            const url = checkActiveResponse.last_log.route.split('/');
            url.splice(0, 1);

            const htmlContent = this.createSolicitudInfoElement(
              checkActiveResponse.last_log.data.creditData!.importe_total!,
              checkActiveResponse.last_log.data.creditData!.fecha_devolucion!,
            );

            const confirmed = await this.errorModalService.openAsync({
              title: 'Tienes una solicitud en curso!\n ',
              content: `¿Deseas retomar la solicitud que dejaste en curso?\n - Importe: ${checkActiveResponse.last_log.data.creditData?.importe_total}`,
              saveButtonLabel: 'Sí, retomar',
              closeButtonLabel: 'No retomar',
              htmlContent,
            });

            if (confirmed) {
              this.destroyRef$.next(true);
              this.dataService.setAppDataSnapshot(checkActiveResponse.last_log.data);
              this.router.navigate(url);
              return;
            } else {
              if (checkActiveResponse.solicitud_id) {
                this.creditService
                  .cancelSolicitud(checkActiveResponse.solicitud_id)
                  .pipe(catchError(() => EMPTY))
                  .subscribe();
              }
            }
          }

          if (this.isModal) this.userLogged.emit(loginResponse.data.token);
          else this.router.navigate([AppModules.CREDIT_REQUEST, CreditRequestRoutes.BANKING_DATA]);
        },
        error: () => {
          this.openModal(
            'Hubo un problema al verificar el código enviado a tu teléfono, por favor revisa que sea correcto y vuelve a intentarlo',
          );
          this.spinner.hide();
          this.loading = false;
        },
      });
  }

  private createSolicitudInfoElement(importe: number, fechaDevolucion: string): HTMLDivElement {
    const divContainer = document.createElement('div');
    const descriptionElement = document.createElement('p');
    const amountElement = document.createElement('strong');
    const importeElement = document.createElement('p');
    const fechaDevolucionElement = document.createElement('p');

    descriptionElement.innerText = 'Deseas retormar la solicitud que dejaste en curso?';
    amountElement.innerText = importe + '€';
    importeElement.innerText = 'Importe: ';
    importeElement.appendChild(amountElement);
    importeElement.style.marginBottom = '8px';
    
    const formattedDate = fechaDevolucion.split('-').reverse().join('/');
    fechaDevolucionElement.innerText = 'Fecha devolucion: ' + formattedDate;
    divContainer.appendChild(descriptionElement);
    divContainer.appendChild(importeElement);
    divContainer.appendChild(fechaDevolucionElement);
    divContainer.style.fontSize = '18px';

    return divContainer;
  }

  openModal(message: string): void {
    const modalData: ModalData = {
      title: '¡Ups! Algo salió mal...',
      closeButtonLabel: 'Cerrar',
      content: message,
      saveButtonLabel: 'Volver al inicio',
      url: '/home',
    };

    this.errorModalService.openModal(modalData);
  }
}
